// MonitorPhone — supervisor's in-dashboard WebRTC phone for Spy/Whisper/Barge.
//
// The backend originates a "Monitor" leg to the supervisor's PJSIP extension
// when they click Spy/Whisper/Barge on the Live Dashboard. For that leg to
// have somewhere to ring, the supervisor's WebRTC endpoint must be REGISTERED
// in the browser. This widget brings up that endpoint and auto-answers the
// monitor leg silently (handled inside useSipEngine).
//
// It only registers its OWN SIP engine when no agent Softphone is already
// mounted (i.e. on the AdminLayout Live Dashboard) — otherwise it would
// double-REGISTER the same extension. On the workspace `/supervisor` route the
// agent Softphone already handles registration + monitor auto-answer, so this
// renders a passive chip.
import { useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { Headphones, Mic, Radio, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useSipEngine } from "@/hooks/useSipEngine";
import { useSoftphoneOptional } from "@/contexts/SoftphoneContext";

export default function MonitorPhone() {
  const hasAgentPhone = !!useSoftphoneOptional();
  // Only enable our own SIP engine when the agent softphone isn't present.
  const sip = useSipEngine({ enabled: !hasAgentPhone });
  const [micGranted, setMicGranted] = useState(
    typeof navigator !== "undefined" && navigator.permissions ? null : true,
  );
  const [micAsking, setMicAsking] = useState(false);

  const askMic = useCallback(async () => {
    setMicAsking(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      stream.getTracks().forEach((t) => t.stop());
      setMicGranted(true);
    } catch {
      setMicGranted(false);
    } finally {
      setMicAsking(false);
    }
  }, []);

  // On the workspace route the agent softphone owns registration.
  if (hasAgentPhone) {
    return (
      <Badge
        variant="outline"
        className="gap-1.5 py-1 px-2.5 border-sky-300 text-sky-700 dark:text-sky-300"
        data-testid="monitor-phone-chip"
      >
        <Headphones className="w-3.5 h-3.5" />
        <span className="text-[11px] font-medium">Monitor via softphone</span>
      </Badge>
    );
  }

  let content;
  const onAir = sip.monitorActive;

  if (sip.mode === "loading") {
    content = (
      <Badge variant="outline" className="gap-1.5 py-1 px-2.5" data-testid="monitor-phone-chip">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span className="text-[11px] font-medium">Monitor phone: connecting…</span>
      </Badge>
    );
  } else if (sip.credError) {
    content = (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className="gap-1.5 py-1 px-2.5 border-red-300 text-red-700 dark:text-red-300 cursor-help"
            data-testid="monitor-phone-chip"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="text-[11px] font-medium">Monitor phone: no extension</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-[240px]">
          {sip.credError} You need a SIP extension + license (Admin → Users) to listen/whisper/barge.
        </TooltipContent>
      </Tooltip>
    );
  } else if (sip.mode === "simulator") {
    content = (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className="gap-1.5 py-1 px-2.5 text-muted-foreground cursor-help"
            data-testid="monitor-phone-chip"
          >
            <Headphones className="w-3.5 h-3.5" />
            <span className="text-[11px] font-medium">Monitor phone: simulator</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-[240px]">
          Preview runs the telephony simulator — Spy/Whisper/Barge controls work but produce no live audio. Verify audio on the live server.
        </TooltipContent>
      </Tooltip>
    );
  } else if (sip.mode === "live" && sip.registered) {
    content = (
      <Badge
        variant="outline"
        className={
          onAir
            ? "gap-1.5 py-1 px-2.5 border-amber-400 text-amber-700 dark:text-amber-300 animate-pulse"
            : "gap-1.5 py-1 px-2.5 border-emerald-400 text-emerald-700 dark:text-emerald-300"
        }
        data-testid="monitor-phone-chip"
      >
        {onAir ? <Radio className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
        <span className="text-[11px] font-medium">
          {onAir ? "Monitor: on air" : "Monitor phone: ready"}
        </span>
      </Badge>
    );
  } else {
    content = (
      <Badge variant="outline" className="gap-1.5 py-1 px-2.5" data-testid="monitor-phone-chip">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span className="text-[11px] font-medium">
          Monitor phone: {sip.registerError ? "error" : "registering…"}
        </span>
      </Badge>
    );
  }

  const needMic = sip.mode === "live" && sip.registered && micGranted === false;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-2" data-testid="monitor-phone">
        {content}
        {needMic && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-[11px] px-2 border-amber-400 text-amber-700"
            onClick={askMic}
            disabled={micAsking}
            data-testid="monitor-phone-enable-mic"
          >
            {micAsking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mic className="w-3 h-3" />}
            Enable mic
          </Button>
        )}
      </div>
    </TooltipProvider>
  );
}
